import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useBrand, useBrands } from '../lib/api/brands';
import { useCampaigns } from '../lib/api/campaigns';
import { useBrandPartnershipResponses } from '../lib/api/partnerships';
import { usePatchTask, useTasks, useTeam } from '../lib/api/tasks';
import { useAssets } from '../lib/api/assets';
import { Button } from '../components/ui/Button';
import { Icon } from '../components/ui/Icon';
import { Chip } from '../components/ui/Chip';
import { DiscountStanceChip, DISCOUNT_STANCE_META } from '../components/ui/DiscountStanceChip';
import { DiscountStance } from '../lib/types';
import { brandInitials, readableOn } from '../lib/brandAvatars';
import { openDataUrl } from '../lib/dataUrl';
import { fmtRange, fmtShort } from '../lib/dates';
import { GanttTimeline } from '../components/gantt/GanttTimeline';
import TaskRow from '../components/tasks/TaskRow';
import { useTaskActions } from '../components/tasks/useTaskActions';
import BrandContactModal from '../components/modals/BrandContactModal';
import BrandShareModal from '../components/modals/BrandShareModal';
import CreateBrandModal from '../components/modals/CreateBrandModal';
import CreateCampaignModal from '../components/modals/CreateCampaignModal';
import CreateAssetModal from '../components/modals/CreateAssetModal';
import { AssetCategory, CampaignAsset } from '../lib/types';
import AttachmentsRow from '../components/attachments/AttachmentsRow';
import NotesCard from '../components/notes/NotesCard';
import BrandComplianceSection from '../components/brands/BrandComplianceSection';
import { useAttachments } from '../lib/api/attachments';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import { useDeleteBrand, useUpdateBrand } from '../lib/api/brands';
import { useToast } from '../components/ui/Toast';
import { assetDisplayTitle } from '../lib/assetTitle';

const typeColor = (t: string) =>
  t === 'Promotion' ? 'var(--sitewide)' : t === 'Brand' ? 'var(--brand)' : 'var(--seasonal)';

/** Asset-card accent per asset category — same color language used on
 *  the campaign-detail asset rows. Newsletter→cobalt, Social→vermilion,
 *  Homepage→green. Used for the left stripe and the leading icon tint. */
const CATEGORY_ACCENT = {
  Email: 'var(--sitewide)',
  'Social Media': 'var(--brand)',
  Homepage: 'var(--seasonal)',
} as const;

/** Inline style for a post-card's colored left stripe. */
const cardAccentStyle = (color: string): React.CSSProperties => ({
  borderLeft: `3px solid ${color}`,
});

/** Inline proof indicators for a post-card. Clicking opens the
 *  screenshot or live URL in a new tab — stopPropagation so the
 *  surrounding card's edit-on-click doesn't trigger. */
const ProofIcons = ({
  screenshot,
  url,
}: {
  screenshot?: string;
  url?: string;
}) => {
  const baseStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 18,
    height: 18,
    borderRadius: 50,
    background: 'var(--bg-subtle)',
    color: 'var(--ink-3)',
    border: 'none',
    cursor: 'pointer',
    flexShrink: 0,
  };
  return (
    <>
      {screenshot && (
        <button
          type="button"
          title="Open screenshot"
          onClick={(e) => {
            e.stopPropagation();
            openDataUrl(screenshot);
          }}
          style={baseStyle}
        >
          <Icon name="image" size={11} />
        </button>
      )}
      {url && (
        <button
          type="button"
          title={`Open ${url}`}
          onClick={(e) => {
            e.stopPropagation();
            window.open(url, '_blank', 'noopener,noreferrer');
          }}
          style={baseStyle}
        >
          <Icon name="link" size={11} />
        </button>
      )}
    </>
  );
};

const SectionLabel = ({
  children,
  action,
}: {
  children: React.ReactNode;
  action?: React.ReactNode;
}) => (
  <div className="section">
    <div className="section-head">
      <div className="section-title">{children}</div>
      {action}
    </div>
  </div>
);

const fmtEUR = (n: number) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n);

/** Small banner line showing how much this brand has committed via
 *  partnerships (sum of effective_price across confirmed responses).
 *  Hidden until there's something to show — no zero-state noise. */
const BrandPartnershipInvestment = ({ brandId }: { brandId: string }) => {
  const { data: responses = [] } = useBrandPartnershipResponses(brandId);
  const confirmed = responses.filter((r) => r.status === 'confirmed');
  if (confirmed.length === 0) return null;
  const total = confirmed.reduce((sum, r) => sum + (Number(r.effective_price) || 0), 0);
  return (
    // Block-level on purpose: it used to sit inline after the margin pills
    // and wrapped mid-row (Doreen, 2026-09-14). Own line, same rhythm.
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
      fontSize: 12.5, color: 'var(--ink-3)', marginBottom: 8,
    }}>
      <span style={{
        fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '0.08em',
        color: 'var(--seasonal)', fontWeight: 600,
        background: 'var(--seasonal-bg)',
        padding: '2px 6px', borderRadius: 4,
      }}>
        Partnership investment
      </span>
      <span style={{ fontWeight: 600, color: 'var(--ink)' }}>{fmtEUR(total)}</span>
      <span style={{ color: 'var(--ink-4)' }}>
        · {confirmed.length} confirmed partnership{confirmed.length === 1 ? '' : 's'}
      </span>
    </div>
  );
};

const BrandDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: brand, isLoading, isError } = useBrand(id);
  const { data: allBrands = [] } = useBrands();
  // Same sort order as the /brands list (alphabetical) so prev/next moves
  // through brands in the order Doreen sees on the overview page.
  const sortedBrands = useMemo(
    () => [...allBrands].sort((a, b) => a.name.localeCompare(b.name)),
    [allBrands],
  );
  const currentIdx = sortedBrands.findIndex((b) => b.id === id);
  const prevBrand = currentIdx > 0 ? sortedBrands[currentIdx - 1] : null;
  const nextBrand = currentIdx >= 0 && currentIdx < sortedBrands.length - 1
    ? sortedBrands[currentIdx + 1]
    : null;

  // Keyboard nav with ← / → arrows. Skip when the user is typing in
  // a text field so we don't hijack normal cursor movement.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      const editable = (e.target as HTMLElement)?.isContentEditable;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || editable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'ArrowLeft' && prevBrand) {
        navigate(`/brands/${prevBrand.id}`);
      } else if (e.key === 'ArrowRight' && nextBrand) {
        navigate(`/brands/${nextBrand.id}`);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [prevBrand, nextBrand, navigate]);
  const { data: campaigns = [] } = useCampaigns(id ? { brand: id } : undefined);
  const { data: tasks = [] } = useTasks(id ? { brand: id } : undefined);
  const { data: posts = [] } = useAssets(id ? { brand: id, category: 'Social Media' } : undefined);
  const { data: newsletters = [] } = useAssets(id ? { brand: id, category: 'Email' } : undefined);
  const { data: homepageItems = [] } = useAssets(id ? { brand: id, category: 'Homepage' } : undefined);
  const { data: team = [] } = useTeam();
  const patchTask = usePatchTask();
  const deleteBrand = useDeleteBrand();
  const updateBrand = useUpdateBrand();
  const toast = useToast();
  const taskActions = useTaskActions();
  const [contactOpen, setContactOpen] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);
  const [campaignOpen, setCampaignOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [assetCategory, setAssetCategory] = useState<AssetCategory | null>(null);
  const [editAsset, setEditAsset] = useState<CampaignAsset | null>(null);
  const [attachmentsOpen, setAttachmentsOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [stanceMenuOpen, setStanceMenuOpen] = useState(false);
  const { data: attachments = [] } = useAttachments({ brand: id ?? '' });

  const setStance = (next: DiscountStance) => {
    if (!brand || brand.discount_stance === next) {
      setStanceMenuOpen(false);
      return;
    }
    updateBrand.mutate(
      { id: brand.id, patch: { discount_stance: next } },
      {
        onSuccess: () => toast.push(`Discount stance set to ${DISCOUNT_STANCE_META[next].label}`),
        onError: () => toast.push('Could not update discount stance'),
      },
    );
    setStanceMenuOpen(false);
  };

  if (isLoading) {
    return (
      <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--ink-4)' }}>
        Loading…
      </div>
    );
  }
  if (isError || !brand) {
    return (
      <div>
        <div className="page-header">
          <div>
            <div className="page-eyebrow">Brand</div>
            <h1 className="page-title">Not found</h1>
            <p className="page-sub">This brand doesn't exist or you can't access it.</p>
          </div>
          <Button variant="ghost" onClick={() => navigate('/brands')}>
            <Icon name="chevronLeft" size={13} /> Back to brands
          </Button>
        </div>
      </div>
    );
  }

  const openTasks = tasks.filter((t) => t.status !== 'Done');
  const topOpenTasks = [...openTasks].sort((a, b) => a.due_date.localeCompare(b.due_date)).slice(0, 10);
  const topPosts = [...posts]
    .sort((a, b) => (a.publish_date ?? '').localeCompare(b.publish_date ?? ''))
    .slice(0, 8);
  const topNewsletters = [...newsletters]
    .sort((a, b) => (a.publish_date ?? '').localeCompare(b.publish_date ?? ''))
    .slice(0, 8);
  const topHomepageItems = [...homepageItems]
    .sort((a, b) => (a.publish_date ?? '').localeCompare(b.publish_date ?? ''))
    .slice(0, 8);

  type ContactView = { name: string; role: string; email: string; phone: string; bits: string[] };
  const buildContact = (name?: string, role?: string, email?: string, phone?: string): ContactView => {
    const c = {
      name: (name || '').trim(),
      role: (role || '').trim(),
      email: (email || '').trim(),
      phone: (phone || '').trim(),
    };
    return { ...c, bits: [c.email, c.phone].filter(Boolean) };
  };
  const primaryContact = buildContact(
    brand.contact_name, brand.contact_role, brand.contact_email, brand.contact_phone,
  );
  const secondaryContact = buildContact(
    brand.contact_name_2, brand.contact_role_2, brand.contact_email_2, brand.contact_phone_2,
  );
  const tertiaryContact = buildContact(
    brand.contact_name_3, brand.contact_role_3, brand.contact_email_3, brand.contact_phone_3,
  );
  const hasContact = (c: ContactView) => !!(c.name || c.role || c.bits.length);
  const hasAnyContact =
    hasContact(primaryContact) || hasContact(secondaryContact) || hasContact(tertiaryContact);

  return (
    <div>
      <div style={{
        marginBottom: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
      }}>
        <button type="button" className="link" onClick={() => navigate('/brands')} style={{ fontSize: 12 }}>
          ← All brands
        </button>
        <span style={{ color: 'var(--ink-5)' }}>·</span>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <button
            type="button"
            onClick={() => prevBrand && navigate(`/brands/${prevBrand.id}`)}
            disabled={!prevBrand}
            title={prevBrand ? `Previous: ${prevBrand.name}` : 'No previous brand'}
            style={{
              display: 'inline-flex', alignItems: 'center',
              padding: '4px 6px', borderRadius: 'var(--r-sm)',
              border: '1px solid var(--border)',
              background: 'var(--bg-elevated)',
              cursor: prevBrand ? 'pointer' : 'not-allowed',
              opacity: prevBrand ? 1 : 0.4,
              color: 'var(--ink-3)',
            }}
          >
            <Icon name="chevronLeft" size={13} />
          </button>
          {currentIdx >= 0 && sortedBrands.length > 0 && (
            <span style={{ fontSize: 11, color: 'var(--ink-4)', padding: '0 4px' }}>
              {currentIdx + 1} / {sortedBrands.length}
            </span>
          )}
          <button
            type="button"
            onClick={() => nextBrand && navigate(`/brands/${nextBrand.id}`)}
            disabled={!nextBrand}
            title={nextBrand ? `Next: ${nextBrand.name}` : 'No next brand'}
            style={{
              display: 'inline-flex', alignItems: 'center',
              padding: '4px 6px', borderRadius: 'var(--r-sm)',
              border: '1px solid var(--border)',
              background: 'var(--bg-elevated)',
              cursor: nextBrand ? 'pointer' : 'not-allowed',
              opacity: nextBrand ? 1 : 0.4,
              color: 'var(--ink-3)',
            }}
          >
            <Icon name="chevronRight" size={13} />
          </button>
        </div>
      </div>

      {/* Business-card hero: avatar + name + country + inline contact + attachments + note */}
      <div
        className="card"
        style={{
          padding: '20px 22px',
          display: 'flex',
          gap: 18,
          alignItems: 'flex-start',
        }}
      >
        <div
          className="brand-hero-mark"
          style={(() => {
            const r = readableOn(brand.color);
            return {
              background: brand.color,
              color: r.color,
              border: r.border,
              boxSizing: 'border-box' as const,
              width: 64,
              height: 64,
              flexShrink: 0,
              overflow: 'hidden',
            };
          })()}
        >
          {brand.logo ? (
            <img
              src={brand.logo}
              alt={brand.name}
              style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
            />
          ) : (
            brandInitials(brand.name)
          )}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            className="page-eyebrow"
            style={{ marginBottom: 2, display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <span>Brand</span>
            <span style={{ color: 'var(--ink-5)' }}>·</span>
            <span title={brand.country_name || undefined}>{brand.country || '—'}</span>
            <span style={{ color: 'var(--ink-5)' }}>·</span>
            <span style={{ position: 'relative', display: 'inline-flex' }}>
              <DiscountStanceChip
                stance={brand.discount_stance}
                onClick={() => setStanceMenuOpen((v) => !v)}
                title="Click to change the discount stance"
              />
              {stanceMenuOpen && (
                <>
                  <div
                    onClick={() => setStanceMenuOpen(false)}
                    style={{ position: 'fixed', inset: 0, zIndex: 40 }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 6px)',
                      left: 0,
                      zIndex: 41,
                      background: 'var(--bg-elevated)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--r-md)',
                      boxShadow: '0 6px 24px rgba(0,0,0,0.10)',
                      padding: 4,
                      minWidth: 220,
                    }}
                  >
                    {(['open', 'negotiate', 'restricted'] as DiscountStance[]).map((s) => {
                      const meta = DISCOUNT_STANCE_META[s];
                      const active = brand.discount_stance === s;
                      return (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setStance(s)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            width: '100%',
                            padding: '6px 8px',
                            border: 'none',
                            background: active ? 'var(--bg-subtle)' : 'transparent',
                            borderRadius: 'var(--r-sm)',
                            cursor: 'pointer',
                            textAlign: 'left',
                            fontSize: 12,
                            color: 'var(--ink-2)',
                          }}
                        >
                          <span
                            style={{
                              width: 9,
                              height: 9,
                              borderRadius: '50%',
                              background: meta.dot,
                              flexShrink: 0,
                            }}
                          />
                          <span style={{ fontWeight: 600 }}>{meta.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </span>
          </div>
          <h1 className="page-title" style={{ fontSize: 26, marginBottom: 6 }}>{brand.name}</h1>
          {brand.permanent_discount && brand.permanent_discount.trim() && (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                marginBottom: 8,
                padding: '3px 8px',
                borderRadius: 999,
                background: 'var(--bg-subtle)',
                color: 'var(--ink-2)',
                fontSize: 11.5,
              }}
              title="Year-round discount + live catalog average. Set automatically from Pricy campaigns and the catalog (weekly sync) — not editable. Auto-fills the discount column on every new partnership row."
            >
              <span style={{ fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-4)', fontWeight: 600 }}>
                Permanent discount
              </span>
              <span style={{ fontWeight: 600 }}>{brand.permanent_discount}</span>
            </div>
          )}
          {brand.margin && brand.margin.trim() && (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                marginBottom: 8,
                marginLeft: 6,
                padding: '3px 8px',
                borderRadius: 999,
                background: 'var(--bg-subtle)',
                color: 'var(--ink-2)',
                fontSize: 11.5,
              }}
              title={brand.margin_source === 'dory'
                ? `Agreed supplier discount off the list price, from Dory's cost rule (${brand.cost_rule}). That is our margin when selling at RRP. Edit it in Dory.`
                : "Measured: average gross margin at RRP across the brand's active catalog, refreshed weekly. Dory has no cost rule for this brand yet."}
            >
              <span style={{ fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-4)', fontWeight: 600 }}>
                Margin
              </span>
              <span style={{ fontWeight: 600 }}>{brand.margin}</span>
            </div>
          )}
          <BrandPartnershipInvestment brandId={brand.id} />

          {/* Identity-card links — website + media bank, shared with Dory */}
          {(() => {
            const site = (brand.website_url || '').trim();
            const bank = (brand.media_bank_url || '').trim();
            const access = (brand.media_bank_access || '').trim();
            const pretty = (u: string) => u.replace(/^https?:\/\//, '').replace(/\/$/, '');
            if (!site && !bank && !access) {
              return (
                <div style={{ fontSize: 12, marginBottom: 8 }}>
                  <button
                    type="button"
                    className="link"
                    style={{ fontSize: 11.5 }}
                    onClick={() => setEditOpen(true)}
                  >
                    Add website & media bank
                  </button>
                </div>
              );
            }
            return (
              <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 8 }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {site && (
                    <a
                      href={site}
                      target="_blank"
                      rel="noreferrer"
                      className="link"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                    >
                      <Icon name="globe" size={11} />
                      {pretty(site)}
                    </a>
                  )}
                  {site && bank && <span style={{ color: 'var(--ink-5)' }}>·</span>}
                  {bank && (
                    <a
                      href={bank}
                      target="_blank"
                      rel="noreferrer"
                      className="link"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
                    >
                      <Icon name="image" size={11} />
                      Media bank
                    </a>
                  )}
                  {access && (
                    <>
                      <span style={{ color: 'var(--ink-5)' }}>·</span>
                      <button
                        type="button"
                        className="link"
                        style={{ fontSize: 11.5 }}
                        onClick={() => setAccessOpen((v) => !v)}
                      >
                        {accessOpen ? 'Hide access info' : 'How to get in'}
                      </button>
                    </>
                  )}
                </div>
                {accessOpen && access && (
                  <div
                    style={{
                      marginTop: 6,
                      padding: '8px 10px',
                      background: 'var(--bg-subtle)',
                      borderRadius: 'var(--r-md)',
                      whiteSpace: 'pre-wrap',
                      color: 'var(--ink-2)',
                      maxWidth: 520,
                    }}
                  >
                    {access}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Inline contact lines (up to 2 contacts) */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              fontSize: 12,
              color: 'var(--ink-3)',
              marginBottom: 10,
            }}
          >
            {hasAnyContact ? (
              <>
                {[primaryContact, secondaryContact, tertiaryContact].filter(hasContact).map((c, idx) => (
                  <div key={idx} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Icon name="user" size={11} />
                    {c.name && (
                      <span style={{ color: 'var(--ink-2)', fontWeight: 500 }}>{c.name}</span>
                    )}
                    {c.role && (
                      <span style={{ color: 'var(--ink-4)' }}>{c.role}</span>
                    )}
                    {c.bits.map((bit, i) => (
                      <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ color: 'var(--ink-5)' }}>·</span>
                        {bit}
                      </span>
                    ))}
                  </div>
                ))}
              </>
            ) : (
              <span style={{ color: 'var(--ink-4)', fontStyle: 'italic' }}>No contact yet</span>
            )}
            <button
              type="button"
              className="link"
              style={{ fontSize: 11.5, alignSelf: 'flex-start' }}
              onClick={() => setContactOpen(true)}
            >
              {hasAnyContact ? 'Edit' : 'Add contact'}
            </button>
          </div>

          {/* Collapsible resource chips — matches campaign-detail pattern */}
          {(() => {
            const hasNotes =
              !!brand.notes && brand.notes.trim().length > 0;
            const fileCount = attachments.length;
            const chipBase: React.CSSProperties = {
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 10px',
              borderRadius: 999,
              fontSize: 11.5,
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.12s',
              border: '1px solid var(--border)',
              background: 'transparent',
            };
            const filled: React.CSSProperties = { color: 'var(--ink-3)' };
            const empty: React.CSSProperties = { color: 'var(--ink-4)' };
            return (
              <>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => setAttachmentsOpen((v) => !v)}
                    style={{
                      ...chipBase,
                      ...(fileCount > 0 ? filled : empty),
                      background: attachmentsOpen
                        ? 'var(--bg-subtle)'
                        : 'transparent',
                    }}
                  >
                    <Icon name="link" size={11} />
                    {fileCount > 0
                      ? `${fileCount} file${fileCount === 1 ? '' : 's'}`
                      : 'Add attachment'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setNotesOpen((v) => !v)}
                    style={{
                      ...chipBase,
                      ...(hasNotes ? filled : empty),
                      background: notesOpen ? 'var(--bg-subtle)' : 'transparent',
                    }}
                  >
                    <Icon name="settings" size={11} />
                    Notes
                    {hasNotes && (
                      <span
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: 50,
                          background: 'var(--accent-1, #3B5CFF)',
                          display: 'inline-block',
                        }}
                      />
                    )}
                  </button>
                </div>
                {attachmentsOpen && (
                  <div style={{ marginTop: 10 }}>
                    <AttachmentsRow brandId={brand.id} />
                  </div>
                )}
                {notesOpen && (
                  <div style={{ marginTop: 10 }}>
                    <NotesCard
                      value={brand.notes ?? ''}
                      onSave={async (notes) => {
                        await updateBrand.mutateAsync({
                          id: brand.id,
                          patch: { notes },
                        });
                      }}
                    />
                  </div>
                )}
              </>
            );
          })()}
        </div>

        <div style={{ display: 'flex', gap: 6, flexDirection: 'column', flexShrink: 0 }}>
          <Button variant="primary" onClick={() => setCampaignOpen(true)}>
            <Icon name="plus" size={13} /> New brand campaign
          </Button>
          {brand.has_visibility_plan ? (
            <Button variant="ghost" onClick={() => navigate(`/brands/${brand.id}/visibility`)}>
              <Icon name="trending" size={13} /> Visibility plan
            </Button>
          ) : (
            <Button
              variant="ghost"
              onClick={async () => {
                try {
                  await updateBrand.mutateAsync({
                    id: brand.id,
                    patch: { has_visibility_plan: true },
                  });
                  navigate(`/brands/${brand.id}/visibility`);
                } catch {
                  toast.push('Could not enable visibility plan');
                }
              }}
            >
              <Icon name="plus" size={13} /> Add visibility plan
            </Button>
          )}
          <Button variant="ghost" onClick={() => navigate(`/brands/${brand.id}/deal/${new Date().getFullYear() + 1}`)}>
            <Icon name="tag" size={13} /> Deal sheet
          </Button>
          <Button variant="ghost" onClick={() => navigate(`/brands/${brand.id}/lookbook`)}>
            <Icon name="image" size={13} /> Lookbook
          </Button>
          <Button variant="ghost" onClick={() => setShareOpen(true)}>
            <Icon name="link" size={13} /> Share plan
          </Button>
          <Button variant="ghost" onClick={() => setEditOpen(true)}>
            <Icon name="settings" size={13} /> Edit
          </Button>
          <Button
            variant="ghost"
            onClick={() => setDeleteOpen(true)}
            style={{ color: 'var(--warn)' }}
          >
            <Icon name="x" size={13} /> Delete
          </Button>
        </div>
      </div>

      {/* REBATE & COMPLIANCE (supplier programmes, e.g. FLOS DEP) */}
      <BrandComplianceSection brandId={brand.id} />

      {/* MARKETING PLAN */}
      {campaigns.length > 0 && (
        <>
          <SectionLabel >Marketing plan</SectionLabel>
          <GanttTimeline
            campaigns={campaigns}
            onCampaignClick={(cid) => navigate(`/campaigns/${cid}`)}
            rangeWeeks={14}
            startOffsetWeeks={-2}
            compact
          />
        </>
      )}

      {/* CAMPAIGNS */}
      <SectionLabel
        
        action={
          <Button variant="ghost" onClick={() => setCampaignOpen(true)}>
            <Icon name="plus" size={12} /> New campaign
          </Button>
        }
      >
        Campaigns
      </SectionLabel>
      <div className="card">
        {campaigns.length === 0 ? (
          <div style={{ padding: 24, color: 'var(--ink-4)' }}>No campaigns tagged with this brand yet.</div>
        ) : (
          [...campaigns]
            .sort((a, b) => (a.start_date ?? '').localeCompare(b.start_date ?? ''))
            .map((c) => (
              <div
                key={c.id}
                className="row"
                style={{ gridTemplateColumns: '1fr 110px 130px' }}
                onClick={() => navigate(`/campaigns/${c.id}`)}
              >
                <div>
                  <div className="row-title">{c.name}</div>
                  <div className="row-meta">
                    {c.parent ? 'nested · ' : ''}
                    {c.brand_details?.length || 0} brand{(c.brand_details?.length ?? 0) === 1 ? '' : 's'}
                  </div>
                </div>
                <Chip kind="type" value={c.type} />
                <div className="mono" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                  {c.start_date && c.end_date ? fmtRange(c.start_date, c.end_date) : 'To plan'}
                </div>
              </div>
            ))
        )}
      </div>

      {/* NEWSLETTERS */}
      <SectionLabel
        
        action={
          <Button variant="ghost" onClick={() => setAssetCategory('Email')}>
            <Icon name="plus" size={12} /> New newsletter
          </Button>
        }
      >
        Newsletters
      </SectionLabel>
      {topNewsletters.length === 0 ? (
        <div className="card" style={{ padding: 20, color: 'var(--ink-4)' }}>
          No newsletters for this brand yet.
        </div>
      ) : (
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
          {topNewsletters.map((n) => {
            const c = campaigns.find((x) => x.id === n.campaign);
            return (
              <div
                key={n.id}
                className="post-card"
                style={{ cursor: 'pointer', ...cardAccentStyle(CATEGORY_ACCENT.Email) }}
                onClick={() => setEditAsset(n)}
              >
                <div className="post-card-header">
                  <span style={{ color: CATEGORY_ACCENT.Email, display: 'inline-flex' }}>
                    <Icon name="mail" size={13} />
                  </span>
                  <span className="mono">{n.publish_date ? fmtShort(n.publish_date) : 'To plan'}</span>
                  <Chip kind="status" value={n.status} />
                </div>
                <div className="post-card-caption" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                    {assetDisplayTitle(n, { withCampaign: true })}
                  </span>
                  <ProofIcons screenshot={n.proof_screenshot} url={n.proof_url} />
                </div>
                <div style={{ fontSize: 11, color: 'var(--ink-4)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 6,
                      height: 6,
                      borderRadius: 50,
                      background: typeColor(c?.type || 'Theme'),
                    }}
                  />
                  {c?.name ?? n.campaign ?? 'Standalone'} · {n.asset_type}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* HOMEPAGE */}
      <SectionLabel
        
        action={
          <Button variant="ghost" onClick={() => setAssetCategory('Homepage')}>
            <Icon name="plus" size={12} /> New homepage item
          </Button>
        }
      >
        Homepage
      </SectionLabel>
      {topHomepageItems.length === 0 ? (
        <div className="card" style={{ padding: 20, color: 'var(--ink-4)' }}>
          No homepage items for this brand yet.
        </div>
      ) : (
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
          {topHomepageItems.map((h) => {
            const c = campaigns.find((x) => x.id === h.campaign);
            return (
              <div
                key={h.id}
                className="post-card"
                style={{ cursor: 'pointer', ...cardAccentStyle(CATEGORY_ACCENT.Homepage) }}
                onClick={() => setEditAsset(h)}
              >
                <div className="post-card-header">
                  <span style={{ color: CATEGORY_ACCENT.Homepage, display: 'inline-flex' }}>
                    <Icon name="image" size={13} />
                  </span>
                  <span className="mono">
                    {!h.publish_date
                      ? 'To plan'
                      : h.end_date && h.end_date !== h.publish_date
                      ? `${fmtShort(h.publish_date)} → ${fmtShort(h.end_date)}`
                      : fmtShort(h.publish_date)}
                  </span>
                  <Chip kind="status" value={h.status} />
                </div>
                <div className="post-card-caption" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                    {assetDisplayTitle(h, { withCampaign: true })}
                  </span>
                  <ProofIcons screenshot={h.proof_screenshot} url={h.proof_url} />
                </div>
                <div style={{ fontSize: 11, color: 'var(--ink-4)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 6,
                      height: 6,
                      borderRadius: 50,
                      background: typeColor(c?.type || 'Theme'),
                    }}
                  />
                  {c?.name ?? h.campaign ?? 'Standalone'} · {h.asset_type}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* SOCIAL POSTS */}
      <SectionLabel
        
        action={
          <Button variant="ghost" onClick={() => setAssetCategory('Social Media')}>
            <Icon name="plus" size={12} /> New post
          </Button>
        }
      >
        Social posts
      </SectionLabel>
      {topPosts.length === 0 ? (
        <div className="card" style={{ padding: 20, color: 'var(--ink-4)' }}>
          No posts for this brand yet.
        </div>
      ) : (
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))' }}>
          {topPosts.map((p) => {
            const c = campaigns.find((x) => x.id === p.campaign);
            return (
              <div
                key={p.id}
                className="post-card"
                style={{ cursor: 'pointer', ...cardAccentStyle(CATEGORY_ACCENT['Social Media']) }}
                onClick={() => setEditAsset(p)}
              >
                <div className="post-card-header">
                  <span style={{ color: CATEGORY_ACCENT['Social Media'], display: 'inline-flex' }}>
                    <Icon name={p.asset_type === 'Facebook Post' ? 'facebook' : 'instagram'} size={13} />
                  </span>
                  <span className="mono">
                    {p.publish_date ? fmtShort(p.publish_date) : 'To plan'}
                    {p.publish_date && p.publish_time ? ` · ${p.publish_time.slice(0, 5)}` : ''}
                  </span>
                  <Chip kind="status" value={p.status} />
                </div>
                <div className="post-card-caption" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                    {assetDisplayTitle(p, { withCampaign: true })}
                  </span>
                  <ProofIcons screenshot={p.proof_screenshot} url={p.proof_url} />
                </div>
                <div style={{ fontSize: 11, color: 'var(--ink-4)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span
                    style={{
                      display: 'inline-block',
                      width: 6,
                      height: 6,
                      borderRadius: 50,
                      background: typeColor(c?.type || 'Theme'),
                    }}
                  />
                  {c?.name ?? p.campaign ?? 'Unassigned'} · {p.asset_type}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* OPEN TASKS */}
      <SectionLabel >Open tasks</SectionLabel>
      <div className="card subdued">
        {topOpenTasks.length === 0 ? (
          <div style={{ padding: 24, color: 'var(--ink-4)' }}>No open tasks for this brand.</div>
        ) : (
          topOpenTasks.map((t) => (
            <TaskRow
              key={t.id}
              task={t}
              team={team}
              showCampaign={false}
              onToggle={() =>
                patchTask.mutate({
                  id: t.id,
                  patch: { status: t.status === 'Done' ? 'In progress' : 'Done' },
                })
              }
              onAssign={(ids) => patchTask.mutate({ id: t.id, patch: { assignees: ids } })}
              onEdit={taskActions.openEdit}
              onDelete={taskActions.openDelete}
            />
          ))
        )}
      </div>

      <BrandContactModal open={contactOpen} onClose={() => setContactOpen(false)} brand={brand} />
      <BrandShareModal open={shareOpen} onClose={() => setShareOpen(false)} brandId={brand.id} brandName={brand.name} />
      <CreateCampaignModal
        open={campaignOpen}
        onClose={() => setCampaignOpen(false)}
        defaultBrandId={brand.id}
        onCreated={(cid) => navigate(`/campaigns/${cid}`)}
      />
      <CreateAssetModal
        open={!!assetCategory}
        onClose={() => setAssetCategory(null)}
        defaultBrandIds={[brand.id]}
        defaultCategory={assetCategory ?? undefined}
      />
      <CreateAssetModal
        open={!!editAsset}
        onClose={() => setEditAsset(null)}
        asset={editAsset}
      />
      <CreateBrandModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        brand={brand}
      />
      <ConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={async () => {
          try {
            await deleteBrand.mutateAsync(brand.id);
            toast.push('Brand deleted');
            navigate('/brands');
          } catch {
            toast.push('Could not delete brand (it may be referenced by campaigns)');
            setDeleteOpen(false);
          }
        }}
        title={`Delete "${brand.name}"?`}
        description={
          campaigns.length > 0
            ? `This brand is referenced by ${campaigns.length} campaign${campaigns.length === 1 ? '' : 's'}. Deleting it will unlink it from them but will not delete the campaigns. This cannot be undone.`
            : 'This will permanently remove the brand. This cannot be undone.'
        }
        confirmLabel="Delete brand"
        danger
        busy={deleteBrand.isPending}
      />
      {taskActions.modals}
    </div>
  );
};

export default BrandDetail;
